// Public UI adapted from Klargo-Reference/pages/PublicHelp.tsx.
import { Link } from 'react-router-dom';
import { Helmet } from '../adapters/Helmet.jsx';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion.jsx';
import Header from '../components/layout/Header.jsx';
import Footer from '../components/layout/Footer.jsx';
import { ForwardArrow, RowChevron } from '../components/ds/StatusMark.jsx';
import { useLanguage } from '../components/layout/LanguageSwitcher.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { HELP_CHAPTERS, helpQuestionKeys, helpRoutesFor } from '../content/help/helpRegister.jsx';
const registerTriggerClass = 'items-start gap-6 pb-[12px] pt-[20px] text-left hover:no-underline [&>svg]:mt-[5px] [&>svg]:h-[14px] [&>svg]:w-[14px] [&>svg]:shrink-0 [&>svg]:stroke-[1.6] [&>svg]:text-ds-muted';
const registerTitleClass = 'font-ds-serif text-[16.5px] font-semibold leading-[1.35] text-ds-ink md:text-[18px]';
const registerContentClass = 'max-w-[62ch] pb-[24px] pr-6 text-[15.5px] leading-[1.65] text-ds-text md:text-[16px]';
const asideLabelClass = 'text-[12.5px] font-semibold uppercase tracking-[0.07em] text-ds-muted';
const PublicHelp = () => {
    const { t } = useLanguage();
    const { isAuthenticated } = useAuth();
    const chapters = HELP_CHAPTERS.map((chapter) => ({
        id: chapter.id,
        title: t(chapter.titleKey),
        lead: t(chapter.leadKey),
        items: chapter.items,
    }));
    return (<div className="ds-scope ds-public-anchors flex min-h-screen min-w-[320px] flex-col bg-ds-canvas font-ds-sans text-[15px] leading-[1.55] text-ds-body antialiased">
      
      <Helmet>
        <title>{t('help.docTitle')}</title>
        <meta name="description" content={t('help.docDescription')}/>
        <meta name="robots" content="index, follow"/>
      </Helmet>

      <Header />

      
      <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
        <div className="border-b border-ds-rule-row pb-10 pt-8 md:pb-12 md:pt-12">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2.5 text-[13.5px] text-ds-muted">
            <Link to={isAuthenticated ? "/user/ChooseAction" : "/"} className="rounded transition-colors hover:text-ds-ink">
              {t('security.breadcrumb.home')}
            </Link>
            <RowChevron />
            <span className="text-ds-body" aria-current="page">
              {t('nav.publicHelp')}
            </span>
          </nav>

          
          <div className="mt-8 max-w-[720px] md:mt-10">
            <h1 className="m-0 font-ds-serif text-[clamp(30px,3.6vw,42px)] font-bold leading-[1.08] tracking-[-0.02em] text-ds-ink [text-wrap:balance]">
              {t('help.hero.title')}
            </h1>
            <p className="mb-0 mt-[18px] max-w-[54ch] text-[16.5px] leading-[1.6] text-ds-text [text-wrap:pretty]">
              {t('help.hero.lead')}
            </p>
          </div>
        </div>
      </div>

      
      
      <div className="mx-auto w-full max-w-[1200px] px-6 pb-16 pt-10 md:px-8 md:pb-20 lg:pt-14">
        
        <div className="lg:grid lg:grid-cols-[232px_minmax(0,720px)] lg:items-stretch lg:gap-x-14">
          
          <aside className="mb-10 border-y border-ds-rule-edge py-5 lg:col-start-1 lg:row-start-1 lg:mb-0 lg:border-0 lg:py-0">
            
            <div className="lg:sticky lg:top-[84px]">
              <div className={`mb-2 ${asideLabelClass}`}>{t('footer.contact.title')}</div>
              
              <p className="m-0 text-[16px] font-semibold leading-[1.35] text-ds-ink">support@klargo.se</p>
              <p className="m-0 mt-1 text-[14.5px] leading-[1.45] text-ds-text">
                {t('ds.support.contactHours')}
              </p>
              <p className="m-0 mt-2 max-w-[34ch] text-[14px] leading-[1.5] text-ds-text [text-wrap:pretty]">
                {t('help.contact.note')}
              </p>
              <a href="mailto:support@klargo.se" data-testid="help-email-cta" className="ds-action-text">
                {t('help.contact.cta')}
                <ForwardArrow stroke="currentColor" width={14} height={11}/>
              </a>
              <p className="m-0 max-w-[34ch] text-[13.5px] leading-[1.5] text-ds-muted [text-wrap:pretty]">
                {t('help.contact.limit')}
              </p>

              
              <nav aria-label={t('help.onThisPage')} className="mt-6 hidden border-t border-ds-rule-row pt-4 lg:block">
                <div className={`mb-1 ${asideLabelClass}`}>{t('help.onThisPage')}</div>
                <ul className="m-0 list-none p-0">
                  {chapters.map((chapter) => (<li key={chapter.id} className="m-0">
                      <a href={`#${chapter.id}`} className="ds-action-text">
                        {chapter.title}
                        <ForwardArrow stroke="currentColor" width={14} height={11}/>
                      </a>
                    </li>))}
                </ul>
              </nav>

              <div className="mt-6 border-t border-ds-rule-row pt-4">
                <div className={`mb-1 ${asideLabelClass}`}>{t('help.readOn')}</div>
                
                <div className="flex flex-col items-start">
                  <Link to="/sakerhet-och-bedragerier" className="ds-action-text">
                    {t('footer.security')}
                    <ForwardArrow stroke="currentColor" width={14} height={11}/>
                  </Link>
                  
                  <Link to="/#hur-det-funkar" className="ds-action-text">
                    {t('Hur det funkar')}
                    <ForwardArrow stroke="currentColor" width={14} height={11}/>
                  </Link>
                </div>
              </div>
            </div>
          </aside>

          <main id="main-content" className="min-w-0 lg:col-start-2 lg:row-start-1">
            
            <div className="space-y-12 md:space-y-[64px]">
              {chapters.map((chapter) => (<section key={chapter.id} id={chapter.id} className="scroll-mt-28">
                  <h2 className="m-0 font-ds-serif text-[clamp(23px,2.5vw,29px)] font-bold leading-[1.15] tracking-[-0.01em] text-ds-ink [text-wrap:balance]">
                    {chapter.title}
                  </h2>
                  <p className="mb-0 mt-[10px] max-w-[62ch] text-[16px] leading-[1.6] text-ds-text [text-wrap:pretty]">
                    {chapter.lead}
                  </p>

                  
                  <Accordion type="single" collapsible className="mt-7 border-t-2 border-ds-ink">
                    {chapter.items.map((item) => (<AccordionItem key={item} value={item} className="border-b border-ds-rule-row">
                        <AccordionTrigger className={registerTriggerClass}>
                          <span className={registerTitleClass}>{t(helpQuestionKeys(item).question)}</span>
                        </AccordionTrigger>
                        <AccordionContent className={registerContentClass}>
                          <p className="m-0">{t(helpQuestionKeys(item).answer)}</p>
                          
                          {helpRoutesFor(item, 'everyone').length > 0 && (<div className="mt-1 flex flex-col items-start">
                              {helpRoutesFor(item, 'everyone').map((route) => route.external ? (<a key={route.to} href={route.to} target="_blank" rel="noopener noreferrer" className="ds-action-text">
                                    {t(route.labelKey)}
                                    <ForwardArrow stroke="currentColor" width={14} height={11}/>
                                  </a>) : (<Link key={route.to} to={route.to} className="ds-action-text">
                                    {t(route.labelKey)}
                                    <ForwardArrow stroke="currentColor" width={14} height={11}/>
                                  </Link>))}
                            </div>)}
                        </AccordionContent>


                      </AccordionItem>))}
                  </Accordion>
                </section>))}
            </div>

            
            <div className="mt-12 border-t border-ds-rule-edge pt-6 md:mt-[72px]">
              <h2 className="m-0 text-[15px] font-semibold leading-[1.4] text-ds-ink">
                {t('help.elsewhere.title')}
              </h2>
              <p className="m-0 mt-2.5 max-w-[62ch] text-[14px] leading-[1.6] text-ds-muted [text-wrap:pretty]">
                {t('help.elsewhere.body')}
              </p>
              <div className="mt-1 flex flex-col items-start sm:flex-row sm:gap-8">
                <Link to="/integritet" className="ds-action-text">
                  {t('footer.legal.privacy')}
                  <ForwardArrow stroke="currentColor" width={14} height={11}/>
                </Link>
                <Link to="/villkor" className="ds-action-text">
                  {t('footer.legal.terms')}
                  <ForwardArrow stroke="currentColor" width={14} height={11}/>
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      <Footer ground="canvas"/>
    </div>);
};
export default PublicHelp;
